import React from 'react';
import styled, { css } from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import { spacing } from '../tokens/spacing';
import { typography } from '../tokens/typography';
import { borders } from '../tokens/borders';
import { shadows } from '../tokens/shadows';
import { generateGradient } from '../../utils/colorUtils';
import { getPatternStyles, PatternType, PatternIntensity } from '../../utils/backgroundPatterns';

/**
 * Divider - Horizontal or vertical divider
 */
export type DividerVariant = 'solid' | 'dashed' | 'dotted' | 'gradient';
export type DividerOrientation = 'horizontal' | 'vertical';

interface DividerProps {
  variant?: DividerVariant;
  orientation?: DividerOrientation;
  thickness?: 'thin' | 'base' | 'thick';
  color?: 'border' | 'primary' | 'secondary' | 'accent' | 'muted';
  spacing?: 'none' | 'sm' | 'md' | 'lg';
  className?: string;
}

const DividerStyled = styled.hr<{
  variant: DividerVariant;
  orientation: DividerOrientation;
  thickness: 'thin' | 'base' | 'thick';
  color: string;
  spacing: string;
}>`
  border: none;
  margin: 0;

  ${props => {
    const thicknesses = {
      thin: '1px',
      base: '2px',
      thick: '4px',
    };
    const thickness = thicknesses[props.thickness];

    if (props.orientation === 'horizontal') {
      return css`
        width: 100%;
        height: ${thickness};
        ${props.variant === 'gradient' 
          ? `background: ${generateGradient(props.color, 'transparent', 'to-right')};`
          : props.variant === 'dashed'
          ? `border-top: ${thickness} dashed ${props.color};`
          : props.variant === 'dotted'
          ? `border-top: ${thickness} dotted ${props.color};`
          : `background: ${props.color};`
        }
        margin-top: ${props.spacing === 'none' ? spacing[0] : spacing[props.spacing as keyof typeof spacing]};
        margin-bottom: ${props.spacing === 'none' ? spacing[0] : spacing[props.spacing as keyof typeof spacing]};
      `;
    } else {
      return css`
        height: 100%;
        width: ${thickness};
        ${props.variant === 'gradient' 
          ? `background: ${generateGradient(props.color, 'transparent', 'to-bottom')};`
          : props.variant === 'dashed'
          ? `border-left: ${thickness} dashed ${props.color};`
          : props.variant === 'dotted'
          ? `border-left: ${thickness} dotted ${props.color};`
          : `background: ${props.color};`
        }
        margin-left: ${props.spacing === 'none' ? spacing[0] : spacing[props.spacing as keyof typeof spacing]};
        margin-right: ${props.spacing === 'none' ? spacing[0] : spacing[props.spacing as keyof typeof spacing]};
      `;
    }
  }}
`;

export const Divider: React.FC<DividerProps> = ({
  variant = 'solid',
  orientation = 'horizontal',
  thickness = 'base',
  color = 'border',
  spacing = 'md',
  className,
}) => {
  const theme = useTheme();
  const colorMap = {
    border: theme.colors.border,
    primary: theme.colors.primary,
    secondary: theme.colors.secondary,
    accent: theme.colors.accent,
    muted: theme.colors.textMuted,
  };
  
  return (
    <DividerStyled
      variant={variant}
      orientation={orientation}
      thickness={thickness}
      color={colorMap[color]}
      spacing={spacing}
      className={className}
    />
  );
};

/**
 * Badge - Status badge component
 */
export type BadgeSize = 'sm' | 'md' | 'lg';
export type BadgeColor = 
  | 'primary' 
  | 'secondary' 
  | 'accent' 
  | 'success' 
  | 'warning' 
  | 'error' 
  | 'info'
  | 'muted';
export type BadgeShape = 'rounded' | 'pill';

interface BadgeProps {
  size?: BadgeSize;
  color?: BadgeColor;
  shape?: BadgeShape;
  icon?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

const BadgeStyled = styled.span<{
  size: BadgeSize;
  color: BadgeColor;
  shape: BadgeShape;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing[1]};
  font-family: ${typography.fontFamily.primary};
  font-weight: ${typography.fontWeight.semibold};
  text-transform: uppercase;
  letter-spacing: ${typography.letterSpacing.wide};

  ${props => {
    const sizes = {
      sm: {
        padding: `${spacing[1]} ${spacing[2]}`,
        fontSize: typography.fontSize.xs,
      },
      md: {
        padding: `${spacing[2]} ${spacing[3]}`,
        fontSize: typography.fontSize.sm,
      },
      lg: {
        padding: `${spacing[3]} ${spacing[4]}`,
        fontSize: typography.fontSize.base,
      },
    };
    const size = sizes[props.size];
    return `
      padding: ${size.padding};
      font-size: ${size.fontSize};
    `;
  }}

  ${props => {
    const radius = props.shape === 'pill' ? borders.radius.full : borders.radius.md;
    return `border-radius: ${radius};`;
  }}

  ${props => {
    const theme = props.theme;
    switch (props.color) {
      case 'primary':
        return css`
          background: ${theme.colors.primary};
          color: ${theme.colors.surface};
        `;
      case 'secondary':
        return css`
          background: ${theme.colors.secondary};
          color: ${theme.colors.surface};
        `;
      case 'accent':
        return css`
          background: ${theme.colors.accent};
          color: ${theme.colors.surface};
        `;
      case 'success':
        return css`
          background: ${theme.colors.success};
          color: ${theme.colors.surface};
        `;
      case 'warning':
        return css`
          background: ${theme.colors.warning};
          color: ${theme.colors.text};
        `;
      case 'error':
        return css`
          background: ${theme.colors.error};
          color: ${theme.colors.surface};
        `;
      case 'info':
        return css`
          background: ${theme.colors.info};
          color: ${theme.colors.surface};
        `;
      case 'muted':
      default:
        return css`
          background: ${theme.colors.surfaceHover};
          color: ${theme.colors.textSecondary};
        `;
    }
  }}
`;

export const Badge: React.FC<BadgeProps> = ({
  size = 'md',
  color = 'primary',
  shape = 'rounded',
  icon,
  children,
  className,
}) => {
  return (
    <BadgeStyled size={size} color={color} shape={shape} className={className}>
      {icon && <span>{icon}</span>}
      {children}
    </BadgeStyled>
  );
};

/**
 * IconWrapper - Icon container with styling
 */
export type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface IconWrapperProps {
  size?: IconSize;
  color?: 'primary' | 'secondary' | 'accent' | 'text' | 'muted';
  hover?: boolean;
  animate?: boolean;
  children: React.ReactNode;
  className?: string;
}

const IconWrapperStyled = styled.span<{
  size: IconSize;
  color: string;
  hover: boolean;
  animate: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.color};
  transition: all 0.2s ease;

  ${props => {
    const sizes = {
      xs: '12px',
      sm: '16px',
      md: '20px',
      lg: '24px',
      xl: '32px',
    };
    return `
      width: ${sizes[props.size]};
      height: ${sizes[props.size]};
      font-size: ${sizes[props.size]};
    `;
  }}

  ${props => props.hover && css`
    cursor: pointer;
    &:hover {
      transform: scale(1.1);
      color: ${props.theme.colors.primary};
    }
  `}

  ${props => props.animate && css`
    animation: pulse 2s ease-in-out infinite;
    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.5; }
    }
  `}
`;

export const IconWrapper: React.FC<IconWrapperProps> = ({
  size = 'md',
  color = 'text',
  hover = false,
  animate = false,
  children,
  className,
}) => {
  const theme = useTheme();
  const colorMap = {
    primary: theme.colors.primary,
    secondary: theme.colors.secondary,
    accent: theme.colors.accent,
    text: theme.colors.text,
    muted: theme.colors.textMuted,
  };
  
  return (
    <IconWrapperStyled
      size={size}
      color={colorMap[color]}
      hover={hover}
      animate={animate}
      className={className}
    >
      {children}
    </IconWrapperStyled>
  );
};

/**
 * AccentLine - Decorative accent line
 */
export type AccentPosition = 'top' | 'bottom' | 'left' | 'right';

interface AccentLineProps {
  position?: AccentPosition;
  color?: 'primary' | 'secondary' | 'accent';
  gradient?: boolean;
  thickness?: 'thin' | 'base' | 'thick';
  length?: 'short' | 'medium' | 'full';
  className?: string;
}

const AccentLineStyled = styled.div<{
  position: AccentPosition;
  color: string;
  gradient: boolean;
  thickness: 'thin' | 'base' | 'thick';
  length: 'short' | 'medium' | 'full';
}>`
  ${props => {
    const thicknesses = {
      thin: '2px',
      base: '4px',
      thick: '6px',
    };
    const lengths = {
      short: '60px',
      medium: '120px',
      full: '100%',
    };
    const thickness = thicknesses[props.thickness];
    const length = lengths[props.length];

    if (props.gradient) {
      const gradient = generateGradient(props.color, 'transparent', 
        props.position === 'top' || props.position === 'bottom' ? 'to-right' : 'to-bottom');
      
      if (props.position === 'top' || props.position === 'bottom') {
        return css`
          width: ${length};
          height: ${thickness};
          background: ${gradient};
          ${props.position === 'top' ? 'top: 0;' : 'bottom: 0;'}
          left: 0;
        `;
      } else {
        return css`
          height: ${length};
          width: ${thickness};
          background: ${gradient};
          ${props.position === 'left' ? 'left: 0;' : 'right: 0;'}
          top: 0;
        `;
      }
    }

    if (props.position === 'top' || props.position === 'bottom') {
      return css`
        width: ${length};
        height: ${thickness};
        background: ${props.color};
        ${props.position === 'top' ? 'top: 0;' : 'bottom: 0;'}
        left: 0;
      `;
    } else {
      return css`
        height: ${length};
        width: ${thickness};
        background: ${props.color};
        ${props.position === 'left' ? 'left: 0;' : 'right: 0;'}
        top: 0;
      `;
    }
  }}

  position: absolute;
`;

export const AccentLine: React.FC<AccentLineProps> = ({
  position = 'top',
  color = 'primary',
  gradient = false,
  thickness = 'base',
  length = 'medium',
  className,
}) => {
  const theme = useTheme();
  const colorMap = {
    primary: theme.colors.primary,
    secondary: theme.colors.secondary,
    accent: theme.colors.accent,
  };
  
  return (
    <AccentLineStyled
      position={position}
      color={colorMap[color]}
      gradient={gradient}
      thickness={thickness}
      length={length}
      className={className}
    />
  );
};

/**
 * PatternOverlay - Background pattern wrapper
 */
interface PatternOverlayProps {
  pattern?: PatternType;
  intensity?: PatternIntensity;
  opacity?: number;
  blendMode?: 'normal' | 'multiply' | 'screen' | 'overlay';
  children: React.ReactNode;
  className?: string;
}

const PatternOverlayStyled = styled.div<{
  pattern?: PatternType;
  intensity: PatternIntensity;
  opacity: number;
  blendMode: string;
}>`
  position: relative;
  
  ${props => {
    if (props.pattern && props.pattern !== 'none') {
      const patternStyles = getPatternStyles(
        props.pattern,
        {
          background: props.theme.colors.background,
          text: props.theme.colors.text,
        },
        props.intensity
      );
      return css`
        &::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
          z-index: 0;
          opacity: ${props.opacity};
          mix-blend-mode: ${props.blendMode};
          background-image: ${patternStyles.backgroundImage};
          background-size: ${patternStyles.backgroundSize};
          background-position: ${patternStyles.backgroundPosition};
        }
        
        > * {
          position: relative;
          z-index: 1;
        }
      `;
    }
    return '';
  }}
`;

export const PatternOverlay: React.FC<PatternOverlayProps> = ({
  pattern = 'dots',
  intensity = 'subtle',
  opacity = 1,
  blendMode = 'normal',
  children,
  className,
}) => {
  return (
    <PatternOverlayStyled
      pattern={pattern}
      intensity={intensity}
      opacity={opacity}
      blendMode={blendMode}
      className={className}
    >
      {children}
    </PatternOverlayStyled>
  );
};
